/**
 * Short-lived cross-app signal that auth just completed on account-web.
 * Cookie (not sessionStorage) so it survives the hard navigate to pro/cert
 * on a different port/subdomain — same sharing rules as auth tokens.
 */

import { clearAuthCookie, getAuthCookie, setAuthCookie } from "./session-cookie";

const WARM_COOKIE = "lion_pro_session_warmed";
const BOOTSTRAP_COOKIE = "lion_pro_user_bootstrap";
/** Long enough for cold Vite boot after hard nav; short enough to not linger. */
const WARM_MAX_AGE_SECONDS = 120;

export type UserBootstrapSnapshot = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileSetupCompleted: boolean;
  preferredLanguage: string | null;
  preferredTimezone: string | null;
};

export function markSessionWarmed(
  bootstrap?: UserBootstrapSnapshot | null,
): void {
  if (typeof window === "undefined") return;
  setAuthCookie(WARM_COOKIE, "1", WARM_MAX_AGE_SECONDS);
  if (bootstrap) {
    try {
      setAuthCookie(
        BOOTSTRAP_COOKIE,
        JSON.stringify(bootstrap),
        WARM_MAX_AGE_SECONDS,
      );
    } catch {
      // ignore quota / serialization failures
    }
  }
}

/** True if a warm handoff cookie is present (does not clear it). */
export function isSessionWarmed(): boolean {
  return getAuthCookie(WARM_COOKIE) === "1";
}

/** Read + clear the warm flag. Returns whether it was set. */
export function consumeSessionWarmed(): boolean {
  const warm = isSessionWarmed();
  if (warm) clearAuthCookie(WARM_COOKIE);
  return warm;
}

export function readUserBootstrapSnapshot(): UserBootstrapSnapshot | null {
  const raw = getAuthCookie(BOOTSTRAP_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as UserBootstrapSnapshot;
    if (!parsed || typeof parsed.id !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearUserBootstrapSnapshot(): void {
  clearAuthCookie(BOOTSTRAP_COOKIE);
}

export function snapshotUserBootstrap(user: {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileSetupCompleted: boolean;
  preferredLanguage?: string | null;
  preferredTimezone?: string | null;
}): UserBootstrapSnapshot {
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    profileSetupCompleted: Boolean(user.profileSetupCompleted),
    preferredLanguage: user.preferredLanguage ?? null,
    preferredTimezone: user.preferredTimezone ?? null,
  };
}
